import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const executives = [
  {
    name: "Chrissy Thompson",
    role: "President & Director",
    bio: "Founder and President of Tandem T Foundation, responsible for strategic leadership, organizational governance, partnerships, fundraising initiatives, and advancing the Foundation's mission.",
  },
  {
    name: "Rohan Taylor",
    role: "Vice President & Director",
    bio: "Provides operational leadership, supports strategic initiatives, and assists in developing partnerships and programs that further the Foundation's mission.",
  },
];

const directors = [
  { name: "Angela Jones", role: "Secretary & Director" },
  { name: "Merlyn Broadbelt", role: "Director of Community Outreach" },
  { name: "Sabrina Redmond", role: "Director of Community Partnerships" },
  { name: "Kirsten Lambay", role: "Director of Strategic Initiatives" },
];

const youthBoard = [
  {
    name: "Faith Thompson",
    role: "Youth Advisory Board Member & Community Ambassador",
    bio: "Faith is dedicated to building strong bridges between our Foundation and the communities we serve. Passionate about connection, she helps welcome volunteers, supports outreach initiatives, and works to ensure that everyone who interacts with Tandem T Foundation feels supported, heard, and valued.",
  },
  {
    name: "Grace Thompson",
    role: "Youth Advisory Board Member & Creative Ambassador",
    bio: "Grace brings vibrant energy, creativity, and imagination to our Foundation's mission. She helps us share our heart with the world through artistic projects, creative visual concepts, and joyful, fresh ideas that bring our charitable initiatives to life.",
  },
  {
    name: "Tayshaun Taylor",
    role: "Youth Advisory Board Member & Senior Operations Lead",
    bio: "Tayshaun brings leadership, structure, and reliable execution to the Foundation's core missions. He supports physical project setups, coordinates on-the-ground volunteer logistics, and works alongside our Junior Operations Lead to help turn big Foundation goals into smooth, successful community initiatives.",
  },
  {
    name: "Christian Thompson",
    role: "Youth Advisory Board Member & Junior Operations Lead",
    bio: "With a sharp mind for strategy and a big heart for service, Christian supports the Foundation's behind-the-scenes operations. He brings analytical thinking and a love for structured teamwork to event planning, project logistics, organizational systems, and digital data — helping turn ideas into organized action.",
  },
];

function SectionLabel({ children }) {
  return (
    <div className="flex items-baseline gap-6">
      <p className="overline text-white/55">{children}</p>
      <div className="flex-1 h-px bg-white/15" />
    </div>
  );
}

export default function Team() {
  useSEO({
    title: "The Team Behind the Dream — The Tandem T Foundation",
    description: "Meet the executive leadership, board of directors, and youth advisory board of The Tandem T Foundation.",
    path: "/foundation/team",
  });

  return (
    <div data-testid="page-team">
      <PageHero
        overline="The Team Behind the Dream"
        title={<>The people <em className="italic">behind the mission.</em></>}
        subtitle="Our leadership and board bring together diverse experience and a shared commitment to advancing the Foundation's mission through integrity, partnership, and service."
        testId="team-hero"
      />

      {/* EXECUTIVE LEADERSHIP */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="team-executive">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionLabel>Executive Leadership</SectionLabel>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {executives.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.06} className="bg-black">
                <div className="p-8 md:p-12 min-h-[300px] flex flex-col">
                  <p className="overline text-white/40">0{i + 1}</p>
                  <p className="font-serif text-white text-3xl md:text-4xl font-light mt-6 leading-tight">{e.name}</p>
                  <p className="text-white/60 font-light mt-2 text-sm">{e.role}</p>
                  <div className="w-10 h-px bg-white/25 my-6" />
                  <p className="text-white/70 font-light text-sm leading-relaxed max-w-md">{e.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BOARD OF DIRECTORS */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="team-board">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionLabel>Board of Directors</SectionLabel>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {directors.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.05} className="bg-black">
                <div className="p-8 min-h-[160px] flex flex-col justify-between">
                  <p className="overline text-white/40">0{i + 3}</p>
                  <div className="mt-6">
                    <p className="font-serif text-white text-2xl font-light leading-tight">{d.name}</p>
                    <p className="text-white/55 font-light mt-2 text-sm">{d.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* YOUTH ADVISORY BOARD */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="team-youth">
        <div className="absolute right-[-6%] top-[10%] w-[42%] max-w-[560px] opacity-[0.05] pointer-events-none select-none hidden md:block" aria-hidden>
          <img src="/brand/tandem-t-logo.png" alt="" className="w-full h-auto" style={{ mixBlendMode: "screen" }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">The Next Generation of Tandem T</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
              We aren&rsquo;t building a Foundation for the next generation. <em className="italic">We&rsquo;re building it with them.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/70 font-light leading-relaxed mt-8 max-w-3xl text-lg">
              Our Youth Advisory Board gives the next generation a seat at the table &mdash; encouraging them to
              share their voices, develop leadership skills, serve their communities, and help shape initiatives
              that impact young people and families.
            </p>
          </Reveal>

          <div className="mt-14">
            <SectionLabel>Youth Advisory Board</SectionLabel>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
              {youthBoard.map((y, i) => (
                <Reveal key={y.name} delay={(i % 2) * 0.06} className="bg-black">
                  <div className="p-8 md:p-10 min-h-[260px] flex flex-col">
                    <p className="overline text-white/40">0{i + 1}</p>
                    <p className="font-serif text-white text-2xl md:text-3xl font-light mt-6 leading-tight">{y.name}</p>
                    <p className="text-white/55 font-light mt-2 text-sm">{y.role}</p>
                    <div className="w-10 h-px bg-white/25 my-6" />
                    <p className="text-white/70 font-light text-sm leading-relaxed max-w-md">{y.bio}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-wrap items-center gap-4">
              <Link to="/foundation/donate" className="group inline-flex items-center gap-3 overline px-8 py-5 bg-white text-black hover:bg-white/85 transition-colors">
                Support Their Work <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
              <Link to="/foundation/contact?intent=volunteer" className="group inline-flex items-center gap-3 overline px-8 py-5 border border-white/25 text-white hover:bg-white hover:text-black transition-colors">
                Join the Team <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
